import { useCallback, useEffect, useState } from "react";
import { BellRing, Clock, Loader2, Mail, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, apiError, fmtCr } from "@/lib/api";

const Picker = ({ label, value, options, onChange, testId }) => (
  <label className="block">
    <span className="label-caps">{label}</span>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="mt-1.5 h-9 w-full border border-border bg-background px-2 text-xs outline-none transition-colors duration-200 focus:border-primary"
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  </label>
);

const HOURS = Array.from({ length: 24 }, (_, h) => [h, `${String(h).padStart(2, "0")}:00`]);
const MINUTES = [0, 15, 30, 45].map((m) => [m, `:${String(m).padStart(2, "0")}`]);

/** SDTD's nudge to the units — what is open, who gets told, and when it goes by itself. */
export const CockpitNudge = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/cockpit/nudge");
      setData(r.data);
      setDraft(r.data.settings);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data || !draft)
    return (
      <div className="flex h-32 items-center justify-center border border-border bg-card">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  const dirty = JSON.stringify(draft) !== JSON.stringify(data.settings);
  const toggleKind = (key) =>
    setDraft((d) => ({
      ...d,
      kinds: d.kinds.includes(key) ? d.kinds.filter((k) => k !== key) : [...d.kinds, key],
    }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/cockpit/nudge", draft);
      toast.success(
        draft.enabled
          ? `Set — the nudge goes out every ${data.weekdays[draft.weekday]} at ${String(
              draft.hour,
            ).padStart(2, "0")}:${String(draft.minute).padStart(2, "0")}.`
          : "The automatic nudge is off. You can still send it by hand.",
      );
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const send = async (dryRun) => {
    setBusy(true);
    try {
      const r = await api.post("/cockpit/nudge/send", {
        kinds: draft.kinds,
        by_mail: draft.by_mail,
        dry_run: dryRun,
      });
      toast.success(dryRun ? `It would go out as: ${r.data.summary}` : r.data.summary);
      if (!dryRun) await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card" data-testid="cockpit-nudge">
      <div className="border-b border-border p-4">
        <div className="label-caps flex items-center gap-2 text-primary">
          <BellRing className="h-3.5 w-3.5" /> Nudge the units
        </div>
        <p className="mt-1.5 max-w-3xl text-[11px] leading-relaxed text-muted-foreground">
          What each unit has not filled in, sent to that unit's SPOC and every member they have
          designated — on their bell inside the portal, and by e-mail as well unless you turn the
          mail off. Press send whenever you like, and set a day and hour for it to go on its own.
        </p>
        <div className="mt-3 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-[11px]">
          <span>
            <b className="font-data text-base">{data.open}</b> item(s) open across{" "}
            <b className="font-data">{data.units.length}</b> unit(s)
          </span>
          <span className="text-muted-foreground">
            {data.officers.length} officer(s) would be written to
          </span>
          {data.next_run_label && (
            <span className="text-muted-foreground">
              next automatic run {data.next_run_label}
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-6 border-b border-border p-4 lg:grid-cols-[1fr_320px]">
        <div>
          <div className="label-caps">What to chase</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(data.all_kinds || []).map((k) => {
              const on = draft.kinds.includes(k.key);
              const total = 0;
              return (
                <button
                  key={k.key}
                  type="button"
                  data-testid={`nudge-kind-${k.key}`}
                  onClick={() => toggleKind(k.key)}
                  title={k.note}
                  className={`font-display border px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 ${
                    on
                      ? "border-primary text-primary"
                      : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                  }`}
                >
                  {k.label}
                  {total ? ` · ${total}` : ""}
                </button>
              );
            })}
          </div>

          <div className="mt-5 max-h-[240px] overflow-y-auto border border-border">
            <table className="w-full text-left text-xs" data-testid="nudge-units">
              <thead className="bg-muted">
                <tr>
                  {["Unit", "Open", "Would be told"].map((h) => (
                    <th key={h} className="label-caps px-3 py-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.units.map((u) => (
                  <tr key={u.plant} data-testid={`nudge-unit-${u.plant}`}>
                    <td className="px-3 py-2 font-medium">{u.name}</td>
                    <td className="font-data px-3 py-2 font-bold">{u.open}</td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground">
                      {u.to.length
                        ? u.to.map((p) => p.name).join(", ")
                        : "no SPOC or member on record — nobody to tell"}
                    </td>
                  </tr>
                ))}
                {!data.units.length && (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">
                      Nothing open anywhere. Every unit has its columns filled in.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-4">
          <label className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-xs">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" /> Send it on its own
            </span>
            <Switch
              checked={draft.enabled}
              onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
              data-testid="nudge-enabled"
            />
          </label>
          <div className="grid grid-cols-3 gap-2">
            <Picker
              label="Day"
              value={draft.weekday}
              testId="nudge-weekday"
              options={(data.weekdays || []).map((w, i) => [i, w])}
              onChange={(v) => setDraft((d) => ({ ...d, weekday: v }))}
            />
            <Picker
              label="Hour"
              value={draft.hour}
              testId="nudge-hour"
              options={HOURS}
              onChange={(v) => setDraft((d) => ({ ...d, hour: v }))}
            />
            <Picker
              label="Minute"
              value={draft.minute}
              testId="nudge-minute"
              options={MINUTES}
              onChange={(v) => setDraft((d) => ({ ...d, minute: v }))}
            />
          </div>
          <label className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-xs">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" /> By e-mail as well as the bell
            </span>
            <Switch
              checked={draft.by_mail}
              onCheckedChange={(v) => setDraft((d) => ({ ...d, by_mail: v }))}
              data-testid="nudge-by-mail"
            />
          </label>
          <Button
            className="w-full rounded-none"
            variant="outline"
            disabled={busy || !dirty}
            onClick={save}
            data-testid="nudge-save"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {dirty ? "Save the schedule" : "Schedule saved"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="min-w-0 text-[11px] text-muted-foreground">
          {data.last_run?.summary ? (
            <>
              <b className="text-foreground">Last sent</b> {data.last_run.summary}
              {data.last_run.at
                ? ` · ${new Date(data.last_run.at).toLocaleString("en-IN")} by ${data.last_run.by}`
                : ""}
            </>
          ) : (
            "Not sent yet."
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-none"
            disabled={busy}
            onClick={() => send(true)}
            data-testid="nudge-dry-run"
          >
            <Users className="mr-2 h-3.5 w-3.5" /> See who it would reach
          </Button>
          <Button
            className="rounded-none font-display text-xs font-bold uppercase tracking-widest"
            disabled={busy || !data.units.length}
            onClick={() => send(false)}
            data-testid="nudge-send"
          >
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send the nudge now
          </Button>
        </div>
      </div>
    </div>
  );
};
